import {
  CrossClientQueryExecution,
  CrossClientQueryExecutionProgress,
  CrossClientQueryPhases,
  CrossClientQueryResultStatuses,
} from '../../../../models/CrossClient'
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { TrackEvent, useTracking } from '../../../../apps/professional-app/fa/common/tracking'
import { useAppSelector } from '../../../../store/hooks'
import { selectCrossClientLandingTeammates, selectCrossClientLandingTeammatesLoading } from '../../../../store/crossClient/crossClientLanding'
import { selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { formatDate, getUserDisplayName } from '../../../../apps/professional-app/fa/common/aifeatures/utils'
import classNames from 'classnames'
import { CrossClientProgress } from '../../Progress/CrossClientProgress'
import { UserAvatar } from '../../../../components/UserAvatar/UserAvatar'
import { getInitials } from '../../../utils'
import { getFormattedDurationShort } from '../../../../common/duration'
import { Tooltip } from '../../../../components/Tooltip/Tooltip'
import { Button } from '../../../../components/Button/Button'
import { CrossClientResultSourceIcon } from '../../Results/Sources/CrossClientResultSourceIcon'
import { sortSources } from '../../Results/Sources/utils'
import { ReactComponent as ZocksIcon } from '../../../../assets/images/ZocksIcon-primary.svg'
import { executionIsRunning } from '../../../../store/crossClient/utils'

interface CrossClientHistoryElementProps {
  execution: CrossClientQueryExecution
  progress?: CrossClientQueryExecutionProgress
  rerunDisabled: boolean
  onStop: (execution: CrossClientQueryExecution) => void
  onRerun: (execution: CrossClientQueryExecution) => void
}

export const CrossClientHistoryElement: React.FC<CrossClientHistoryElementProps> = ({ execution, progress, rerunDisabled, onStop, onRerun }) => {
  const navigate = useNavigate()

  const { tracked } = useTracking()

  const teammates = useAppSelector(selectCrossClientLandingTeammates)
  const teammatesLoading = useAppSelector(selectCrossClientLandingTeammatesLoading)
  const currentUser = useAppSelector(selectSafeAuthenticatedUser)

  const [now, setNow] = useState<Date>(new Date())
  const [hovered, setHovered] = useState<boolean>(false)
  const [showStopTooltip, setShowStopTooltip] = useState<boolean>(false)

  const isRunning = execution?.phase === CrossClientQueryPhases.PROCESSING
  const userName = useMemo(() => {
    return execution.createdBy?.id === currentUser.id
      ? getUserDisplayName(currentUser)
      : getUserDisplayName(teammates.find((t) => t.id === execution.createdBy?.id))
  }, [currentUser, execution.createdBy?.id, teammates])

  useEffect(() => {
    if (isRunning) {
      const interval = setInterval(() => setNow(new Date()), 1000)
      return () => {
        clearInterval(interval)
      }
    }
  }, [isRunning])

  const handleExecutionClicked = tracked(() => navigate(`/cross-client/executions/${execution.id}`), {
    eventName: TrackEvent.X_CLIENT_HISTORY_OPENED,
    eventDetails: {
      executionId: execution.id,
    },
  })

  const initializedResultCount = progress?.statuses[CrossClientQueryResultStatuses.INITIALIZED] ?? 0
  const succeededResultCount = progress?.statuses[CrossClientQueryResultStatuses.SUCCEEDED] ?? 0
  const noDataResultCount = progress?.statuses[CrossClientQueryResultStatuses.NO_DATA] ?? 0
  const failedResultCount = progress?.statuses[CrossClientQueryResultStatuses.FAILED] ?? 0
  const processingResultCount = progress?.statuses[CrossClientQueryResultStatuses.PROCESSING] ?? 0
  const processedResultCount = succeededResultCount + noDataResultCount + failedResultCount
  const totalResultCount = initializedResultCount + processedResultCount + processingResultCount

  const handleStopExecution = (e: React.MouseEvent) => {
    e.stopPropagation()
    onStop(execution)
  }

  const handleRerunExecution = (e: React.MouseEvent) => {
    e.stopPropagation()
    onRerun(execution)
  }

  const stopped = execution.phase === CrossClientQueryPhases.CANCELED

  return (
    <div
      className={classNames(
        'd-flex gap-3 p-3 align-items-center border border-1  zocks-border-0.75 bg-white w-100 cross-client-history-element cross-client-transition',
        {
          'zocks-border-color-primary cross-client-history-element-box-shadow': hovered,
          'zocks-border-color-gray-300': !hovered,
        },
      )}
      role='button'
      onClick={handleExecutionClicked}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <>
        <CrossClientProgress
          className='cross-client-landing-progress-container'
          total={execution.resultCount}
          done={processedResultCount}
          lineWidth={35}
          doneColor={stopped ? 'var(--zocks-gray-500)' : 'var(--zocks-primary)'}
        />
        <div className='d-flex zocks-gap-2 justify-content-between align-items-center w-100'>
          <div className='d-flex flex-column gap-1 flex-grow-1 w-100'>
            <div className='paragraph-medium medium text-truncate cross-client-history-question'>{execution.userQuestion}</div>
            <div className='d-flex gap-3'>
              <div className='d-flex gap-2 align-items-center'>
                <ZocksIcon width='1rem' height='1rem' />
                {sortSources(execution.sources ?? []).map((source, idx) => (
                  <CrossClientResultSourceIcon key={idx} source={source} size='md' />
                ))}
              </div>
              <div className='d-flex gap-1 align-items-center'>
                {teammatesLoading && !userName ? (
                  <div className='skeleton' style={{ width: '6rem', height: '1rem' }} />
                ) : (
                  <>
                    <UserAvatar
                      width='1rem'
                      height='1rem'
                      fontSize='0.5rem'
                      className='lead-extra-small zocks-text-white zocks-bg-color-gray-900'
                      useInitials
                      initialLetters={getInitials(userName.split(' ')[0])}
                    />
                    <div className='paragraph-extra-small'>{userName}</div>
                  </>
                )}
              </div>
              <div className='d-flex gap-1 align-items-center'>
                <i className='bi bi-calendar' />
                <div className='paragraph-extra-small'>{formatDate(execution.createdAt)}</div>
              </div>
              <div className='d-flex gap-1 align-items-center'>
                {!progress ? null : !stopped ? (
                  <>
                    <i className='bi bi-stopwatch' />
                    {execution.phase === CrossClientQueryPhases.COMPLETED ? (
                      <div className='paragraph-extra-small zocks-text-gray-700'>{getFormattedDurationShort(execution.createdAt, execution.updatedAt)}</div>
                    ) : (
                      <div className='paragraph-extra-small zocks-text-gray-700'>{getFormattedDurationShort(execution.createdAt, now.toISOString())}</div>
                    )}
                  </>
                ) : (
                  <>
                    <i className='bi bi-stop-fill' />
                    <div className='paragraph-extra-small zocks-text-gray-700'>
                      Stopped {progress && totalResultCount ? `at ${Math.floor((processedResultCount / totalResultCount) * 100)}%` : null}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className='d-flex gap-3 align-items-center'>
            {!progress ? null : executionIsRunning(progress) ? (
              <Tooltip className='zocks-text-gray-500' placement='top' title={showStopTooltip ? 'Stop query' : ''} show={showStopTooltip}>
                <Button
                  variant='header'
                  className='px-2 zocks-border-0.5'
                  onClick={handleStopExecution}
                  onMouseEnter={() => setShowStopTooltip(true)}
                  onMouseLeave={() => setShowStopTooltip(false)}
                >
                  <i className='bi bi-stop-fill' />
                </Button>
              </Tooltip>
            ) : progress.phase === CrossClientQueryPhases.COMPLETED || progress.phase === CrossClientQueryPhases.CANCELED ? (
              <Tooltip className='zocks-text-gray-500' placement='top' title='Run this query again'>
                <Button variant='header' className='px-2 zocks-border-0.5' onClick={handleRerunExecution} disabled={rerunDisabled}>
                  <i className='bi bi-arrow-clockwise' />
                </Button>
              </Tooltip>
            ) : null}
            <i className={classNames('bi bi-arrow-right fs-5', { 'zocks-text-primary': hovered })} />
          </div>
        </div>
      </>
    </div>
  )
}
